import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FilePageTabType } from '../components/files/file-manager/file-manager.component';
import { routeParamToTabId } from '../components/user-relation-requests/components/manage-relation-request/manage-relation-request.helper';
import { RelationPageTabs } from '../components/user-relation-requests/relation-request.enum';
import { isSatisfyUnion, MaybeUndefined } from '../utils';

export enum PagesTitle {
  SIGN_UP_PAGE = 'SIGN_UP_PAGE_TITLE',
  LOGIN_PAGE = 'LOGIN_PAGE_TILE',
  RESET_PASSWORD_PAGE = 'RESET_PASSWORD_PAGE_TITLE',
  PASSWORD_RECOVERY_PAGE = 'PASSWORD_RECOVERY_PAGE_TITLE',
  CHANGE_PASSWORD_PAGE = 'CHANGE_PASSOWORD_PAGE_TITLE',
  SECOND_AUTH_LEVEL_PAGE = 'SECOND_AUTH_LEVEL_PAGE_TITLE',
  BIND_AUTHENTICATOR_PAGE = 'BIND_AUTHENTICATOR_PAGE_TITLE',
  ACCEPT_ADMIN_SIGN_UP_INVITATION_PAGE = 'ACCEPT_ADMIN_SIGN_UP_INVITATION_PAGE_TITLE',

  ABOUT_US_PAGE = 'ABOUT_US_PAGE_TILE',
  TERMS_PAGE = 'TERMS_PAGE_TILE',
  DONATE_PAGE = 'DONATE_PAGE_TITLE',
  CONTACT_US_PAGE = 'CONTACT_US_PAGE_TITLE',

  VIEW_PROFILE_PAGE = 'VIEW_PROFILE_PAGE_TITLE',
  EDIT_PROFILE_PAGE = 'EDIT_PROFILE_PAGE_TITLE',

  MAP_PAGE = 'MAP_PAGE_TILE',
  SEARCH_PAGE = 'SEARCH_PAGE_TITLE',

  DOCTOR_LIST_PAGE = 'DOCTOR_LIST_PAGE_TITLE',
  REQUEST_TO_DOCTOR_PAGE = 'REQUEST_TO_DOCTOR_PAGE_TITLE',
  REQUEST_FROM_DOCTOR_PAGE = 'REQUEST_FROM_DOCTOR_PAGE_TITLE',

  PATIENT_LIST_PAGE = 'PATIENT_LIST_PAGE_TITLE',
  REQUEST_TO_PATIENT_PAGE = 'REQUEST_TO_PATIENT_PAGE_TITLE',
  REQUEST_FROM_PATIENT_PAGE = 'REQUEST_FROM_PATIENT_PAGE_TITLE',

  CONNECTION_LIST_PAGE = 'CONNECTION_LIST_PAGE_TITLE',
  SENT_CONNECTION_PAGE = 'SENT_CONNECTION_PAGE_TITLE',
  RECEIVED_CONNECTION_PAGE = 'RECEIVED_CONNECTION_PAGE_TITLE',
  MESSAGE_TO_GROUP_PAGE = 'MESSAGE_TO_GROUP_PAGE_TITLE',

  CHAT_PAGE = 'CHAT_PAGE_TITLE',
  BLOCKED_PAGE = 'BLOCKED_PAGE_TITLE',

  DOCUMENTS_PAGE = 'DOCUMENTS_PAGE_TITLE',
  IMAGES_PAGE = 'IMAGES_PAGE_TITLE',
  VIDEO_PAGE = 'VIDEO_PAGE_TITLE',
  AUDIO_PAGE = 'AUDIO_PAGE_TITLE',
  FILES_ACTIVITY_PAGE = 'FILES_ACTIVITY_PAGE_TITLE',

  PROJECTS_LIST_PAGE = 'PROJECTS_LIST_PAGE_TITLE',
  CREATE_PROJECT_PAGE = 'CREATE_PROJECT_PAGE_TITLE',
  PROJECT_VIEW_PAGE = 'PROJECT_VIEW_PAGE_TITLE',
  PROJECT_EDIT_PAGE = 'PROJECT_EDIT_PAGE_TITLE',
  SOCIAL_GROUP_INVITE_PAGE = 'SOCIAL_GROUP_INVITE_PAGE_TITLE',

  COMMUNITIES_LIST_PAGE = 'COMMUNITIES_LIST_PAGE_TITLE',
  CREATE_COMMUNITY_PAGE = 'CREATE_COMMUNITY_PAGE_TITLE',
  COMMUNITY_VIEW_PAGE = 'COMMUNITY_VIEW_PAGE_TITLE',
  COMMUNITY_EDIT_PAGE = 'COMMUNITY_EDIT_PAGE_TITLE',
  INVITE_TO_COMMUNITY_PAGE = 'INVITE_TO_COMMUNITY_PAGE_TITLE',

  POSTS_LIST_PAGE = 'POSTS_LIST_PAGE_TITLE',
  POST_VIEW_PAGE = 'POST_VIEW_PAGE_TITLE',
  POST_EDIT_PAGE = 'POST_EDIT_PAGE_TITLE',
  CREATE_POST_PAGE = 'CREATE_POST_PAGE_TITLE',

  DISEASES_AND_SPECIALTIES_PAGE = 'DISEASES_AND_SPECIALTIES_PAGE_TITLE',
  CHARITY_FOCUSES_PAGE = 'CHARITY_FOCUSES_PAGE_TITLE',

  DEACTIVATED_USER_PAGE = 'DEACTIVATED_USER_PAGE_TITLE',
  ADMINS_LIST_PAGE = 'ADMINS_LIST_PAGE_TITLE',

  PREFERENCES_PAGE = 'PREFERENCES_PAGE_TITLE',

  DEFAULT_TITLE = '1Life2Connect™',
}

export interface EntityPagesTitle {
  viewPage: string;
  editPage: string;
}

@Injectable({ providedIn: 'root' })
export class AppPageTitleStrategy extends TitleStrategy {
  protected title = inject(Title);
  protected translateService = inject(TranslateService);

  protected translate: (text: string) => string = (text) => this.translateService.instant(text) as string;

  public updateTitle(snapshot: RouterStateSnapshot): void {
    const title: string = this.buildTitle(snapshot) ?? PagesTitle.DEFAULT_TITLE;

    this.title.setTitle(this.translate(title));
  }
}

export const filePageTitleResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
  const titles: Record<FilePageTabType, PagesTitle> = {
    audio: PagesTitle.AUDIO_PAGE,
    video: PagesTitle.VIDEO_PAGE,
    images: PagesTitle.IMAGES_PAGE,
    documents: PagesTitle.DOCUMENTS_PAGE,
    activities: PagesTitle.FILES_ACTIVITY_PAGE,
  };

  const type: MaybeUndefined<string> = route.queryParamMap.get('type');

  if (isSatisfyUnion<FilePageTabType>(type, ['audio', 'video', 'images', 'documents', 'activities'])) {
    return titles[type];
  }

  return PagesTitle.DEFAULT_TITLE;
};

export const connectionPageTitleResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
  const titles: Record<RelationPageTabs, PagesTitle> = {
    [RelationPageTabs.LIST]: PagesTitle.CONNECTION_LIST_PAGE,
    [RelationPageTabs.REQUESTS_SENT]: PagesTitle.SENT_CONNECTION_PAGE,
    [RelationPageTabs.REQUESTS_RECEIVED]: PagesTitle.RECEIVED_CONNECTION_PAGE,
  };

  const tab: RelationPageTabs = routeParamToTabId(route.paramMap.get('type'), RelationPageTabs.LIST);

  return titles[tab];
};

export const doctorPageTitleResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
  const titles: Record<RelationPageTabs, PagesTitle> = {
    [RelationPageTabs.LIST]: PagesTitle.DOCTOR_LIST_PAGE,
    [RelationPageTabs.REQUESTS_SENT]: PagesTitle.REQUEST_TO_DOCTOR_PAGE,
    [RelationPageTabs.REQUESTS_RECEIVED]: PagesTitle.REQUEST_FROM_DOCTOR_PAGE,
  };

  const tab: RelationPageTabs = routeParamToTabId(route.paramMap.get('type'), RelationPageTabs.LIST);

  return titles[tab];
};

export const patientPageTitleResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
  const titles: Record<RelationPageTabs, PagesTitle> = {
    [RelationPageTabs.LIST]: PagesTitle.PATIENT_LIST_PAGE,
    [RelationPageTabs.REQUESTS_SENT]: PagesTitle.REQUEST_TO_PATIENT_PAGE,
    [RelationPageTabs.REQUESTS_RECEIVED]: PagesTitle.REQUEST_FROM_PATIENT_PAGE,
  };

  const tab: RelationPageTabs = routeParamToTabId(route.paramMap.get('type'), RelationPageTabs.LIST);

  return titles[tab];
};
